import { ajv, compile } from '@symon-ai/wisepipe-validate';
import { addDefinition } from '../classes/object-definition';
import { PropertyDefinition } from '../classes/property-definition';
/* eslint-disable func-names, no-restricted-syntax, guard-for-in, no-param-reassign */
/*
let INIT_VALIDATE_COUNT = 0;
export function ConsCount(): number {
    return INIT_VALIDATE_COUNT;
}

let SCHEMA_COUNT = 0;
export function SchemaCount(): number {
    return SCHEMA_COUNT;
}

// Validation observation for performance optimization
let VALIDATIONS = 0;
export function ValidationCount(): number {
    return VALIDATIONS;
}
*/
const schemaIDs = new Map();
function hasSet(properties) {
    if (properties instanceof Object) {
        for (const key in properties) {
            if (key === 'type' && properties[key] === 'set') {
                return true;
            }
            if (hasSet(properties[key])) {
                return true;
            }
        }
    }
    return false;
}
function convertSetToArray(properties) {
    if (properties instanceof Object) {
        for (const key in properties) {
            if (key === 'type' && properties[key] === 'set') {
                properties[key] = 'array';
            }
            else {
                convertSetToArray(properties[key]);
            }
        }
    }
}
// This allows us to use 'set' as a type. This serialization library already supported it by turning it into
// an array transparently. The issue is 'set' is not a keyword for Ajv. This function swaps the word 'set' for
// 'array' in order to produce a JSON schema that is valid and correct for the way this type will end up being
// serialized.
function convertToAjvSchema(schema) {
    if (!schema.properties || !hasSet(schema.properties))
        return schema;
    const copy = JSON.parse(JSON.stringify(schema));
    convertSetToArray(copy.properties);
    return copy;
}
function configureProperty(property, key, types) {
    const funcs = types.get(key);
    // the Function[] is for anyOf, allOf, etc, as well as for arrays that are anyOf or allOf (like case config)
    // in serialize+deserialize, we use try to match the value being serde'd into the first Function that matches
    if (funcs.length === 1) {
        const type = funcs[0];
        property.array = type === Array;
        property.set = type === Set;
        if (property.array || property.set) {
            const itemsKey = `${key}.items`;
            const itemsType = types.get(itemsKey);
            if (!itemsType) {
                throw new Error(`Missing items property on array ${key}`);
            }
            property.types = itemsType;
        }
        else if (!property.type) {
            property.type = type;
        }
    }
    else {
        property.types = funcs;
    }
}
function addProperty(definition, key, types) {
    const property = definition.getProperty(key);
    configureProperty(property, key, types);
}
function getCompounds(schema) {
    let compounds = [];
    if (schema.not) {
        compounds.push(schema.not);
    }
    if (schema.if) {
        compounds.push(schema.then);
        if (schema.else) {
            compounds.push(schema.else);
        }
    }
    if (schema.switch) {
        for (const cse of schema.switch) {
            compounds.push(cse.then);
        }
    }
    if (schema.allOf) {
        compounds = compounds.concat(schema.allOf);
    }
    if (schema.anyOf) {
        compounds = compounds.concat(schema.anyOf);
    }
    if (schema.oneOf) {
        compounds = compounds.concat(schema.oneOf);
    }
    return compounds;
}
function merge(a, b) {
    // get types of all child schemas
    for (const key of b.keys()) {
        if (a.has(key)) {
            // use the set of all types; sometimes there's if/else and both are "number" types - this will merge it
            const set = new Set(a.get(key).concat(b.get(key)));
            a.set(key, Array.from(set));
        }
        else {
            a.set(key, b.get(key));
        }
    }
    return a;
}
function setPropertyType(schema, key, types) {
    if (schema.type) {
        const { type } = schema;
        if (typeof type === 'string') {
            switch (type.toLowerCase()) {
                case 'array':
                    if (schema.items) {
                        setPropertyType(schema.items, `${key}.items`, types);
                    }
                    types.set(key, [Array]);
                    return;
                case 'set':
                    if (schema.items) {
                        setPropertyType(schema.items, `${key}.items`, types);
                    }
                    types.set(key, [Set]);
                    return;
                case 'boolean':
                    types.set(key, [Boolean]);
                    return;
                case 'object':
                    types.set(key, [Object]);
                    return;
                case 'number':
                case 'integer':
                    types.set(key, [Number]);
                    return;
                case 'string':
                    types.set(key, [String]);
                    return;
                default:
                    types.set(key, [Object]); // could go further here. don't believe we need to for serialization.
                    return;
            }
        }
        if (Array.isArray(type)) {
            // array of multiple types
            types.set(key, [Object]); // could go further here. don't believe we need to for serialization.
            return;
        }
    }
    if (schema.$ref) {
        // lookup the referenced schema to get type
        const id = schema.$ref;
        if (typeof id === 'string') {
            const target = schemaIDs.get(id);
            if (target) {
                types.set(key, [target]);
                return;
            }
            types.set(key, [Object]);
            return;
        }
    }
    const compounds = getCompounds(schema);
    if (compounds.length > 0) {
        const schemas = [];
        for (const sch of compounds) {
            const map = new Map();
            setPropertyType(sch, key, map);
            schemas.push(map);
        }
        const merged = schemas.reduce(merge);
        merge(types, merged);
        return;
    }
    throw new Error(`Missing type or $ref on property ${key} - got ${schema}`);
}
function getProperties(properties) {
    const map = new Map();
    for (const key of Object.keys(properties)) {
        setPropertyType(properties[key], key, map);
    }
    return map;
}
function getSchemaProperties(schema) {
    let props = [];
    if (schema.properties) {
        props.push(getProperties(schema.properties));
    }
    const compounds = getCompounds(schema);
    props = props.concat(compounds.map(getSchemaProperties));
    const output = props.reduce(merge, new Map());
    return output;
}
function getRefs(target, refs) {
    // Gets all schema references recursively
    // is this an object?
    if (target === null || typeof target !== 'object')
        return;
    // recursively capture all its $ref values
    for (const key of Object.keys(target)) {
        const value = target[key];
        if (key === '$ref') {
            refs.add(value);
        }
        else {
            getRefs(value, refs);
        }
    }
}
function initializeValidate(constructor) {
    // Already initialized?
    const definition = addDefinition(constructor);
    const schema = definition.schema;
    if (!schema)
        return;
    // ++INIT_VALIDATE_COUNT;
    // Initialize validate for all dependencies. Ajv cannot compile a schema validation functions until we've added
    // all of the schemas it references to ajv.
    const refs = new Set();
    getRefs(schema, refs);
    if (schema.$id) {
        // schemas may recursively reference themselves, those aren't dependencies
        refs.delete(schema.$id);
    }
    for (const ref of refs) {
        const constructor = schemaIDs.get(ref);
        if (!constructor)
            throw new Error(`Could not resolve reference ${ref} from ${schema}.`);
        initializeValidate(constructor);
    }
    // Add our schema
    const ajvSchema = convertToAjvSchema(schema);
    if (schema.$id && ajv.getSchema(schema.$id) === undefined) {
        ajv.addSchema(ajvSchema, schema.$id);
    }
    // Use ajv to compile the validation function
    const validate = compile(ajvSchema);
    // Replace stub validation function with our real one
    definition.validate = validate;
    /*definition.validate = (input: any) => {
        ++VALIDATIONS;
        validate(input);
    };*/
    // Mark validate as initialized
    delete definition.schema;
}
export function JsonSchema(schema) {
    // This is called once for every decoratored class. This happens during initialization for all decorated classes. We
    // now have hundreds of domain objects in each service and this code runs once for each of them at startup. Given that,
    // we'd like to defer as much of this work as we can. The vast majority of domain objects are not used by any given
    // endpoint.
    // ++SCHEMA_COUNT;
    return function (constructor) {
        // This function is called once for every decoratored class, after JsonSchema.
        // 1. Support validation - defer validate() function setup to initial validation call, performed by initializeValidate()
        const definition = addDefinition(constructor);
        definition.schema = schema;
        if (schema.$id) {
            schemaIDs.set(schema.$id, constructor);
        }
        definition.validate = (input) => {
            // Initialize validate directly replaces definition.validate so the validate!() call is to that newly initialized
            // function, not an infinite loop as it might appear here.
            initializeValidate(constructor);
            definition.validate(input);
        };
        // 2. Support serialization - Unfortunately it works by crawling all over the definition. There isn't a function we can
        // prepare just-in-time like validation.
        // get properties from schema
        const props = getSchemaProperties(schema);
        // add root properties for serialization
        for (const key of props.keys()) {
            if (key.indexOf('.') >= 0)
                continue; // filter out compound keys (e.g. key.items)
            addProperty(definition, key, props);
        }
        // Support for the 'additionalProperties' field of JSON schema. All extra properties
        // will get seriailized as this type.
        if (schema.additionalProperties) {
            const types = new Map();
            setPropertyType(schema.additionalProperties, 'additionalProperties', types);
            const additional = new PropertyDefinition('additionalProperties');
            configureProperty(additional, 'additionalProperties', types);
            definition.additionalProperties = additional;
        }
    };
}
//# sourceMappingURL=json-schema.js.map